import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { FiArrowRight, FiCheck } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { GlassFilter } from './liquid-glass';

// ─── Services data ─────────────────────────────────────────────────────────────
const SERVICES = [
  {
    icon: <img src="/logodelivex.png" alt="DeliveX" className="h-12 w-12 rounded-xl object-contain sm:h-16 sm:w-16 sm:rounded-2xl" />,
    miniIcon: <img src="/logodelivex.png" alt="DeliveX" className="h-8 w-8 rounded-lg object-contain sm:h-9 sm:w-9 sm:rounded-xl" />,
    title: 'DeliveX',
    subtitle: 'Plataforma Completa para Gestão de Restaurantes e Delivery',
    description:
      'Plataforma desenvolvida para centralizar toda a operação de restaurantes, lanchonetes e pizzarias. O DeliveX integra atendimento, vendas, produção, entregas e relacionamento com clientes em um único sistema.',
    features: [
      'Cardápio 100% Digital e Pedidos Online',
      'Nenhuma Cobrança por Venda, Apenas Mensalidade Fixa',
      'Fidelidade, Cupons e Relacionamento com Clientes',
      'Integração com Lucrum - Sistema Financeiro',
      'Integração com Rotas - Gestão de Entregas',
      'Integração com PapIA - WhatsApp com IA ',
    ],
    results: [
      'Lucro de até 30% com Pedidos Diretos, sem Taxas de Aplicativos',
      'Redução no Tempo de Atendimento e Entrega',
      'Aumento da Satisfação do Cliente',
      'Maior Retorno de Clientes e Fidelização',
    ],
    website: 'https://delivery.aytech.online/',
    showcaseImage: '/hashidel.png',
    wideShowcase: true,
    showcasePosition: 'left',
    showcaseSize: 'square',
    showcaseAlign: 'start',
    price: 'A partir de R$ 149,90/mês',
    slug: 'delivex',
  },
  {
    icon: <img src="/logopapia.png" alt="PapIA" className="h-12 w-12 rounded-xl object-contain sm:h-16 sm:w-16 sm:rounded-2xl" />,
    miniIcon: <img src="/logopapia.png" alt="PapIA" className="h-8 w-8 rounded-lg object-contain sm:h-9 sm:w-9 sm:rounded-xl" />,
    title: 'PapIA',
    subtitle: 'Inteligência Artificial para WhatsApp e Instagram',
    description:
      'Sistema completo de automação para WhatsApp e Instagram, com IA conversacional, CRM integrado em fase de testes, dashboard avançado para gerenciamento e relatórios detalhados de performance.',
    features: [
      'Automação Inteligente de Respostas',
      'CRM com Histórico Completo',
      'Analytics em Tempo Real',
      'Integração com Outros Sistemas',
      'Chatbot com IA Conversacional',
      'Agendamentos em Fase de Testes',
    ],
    results: [
      '+200% na Conversão de Leads',
      '-95% no Tempo de Resposta',
      '92% das Tarefas Automatizadas',
      '+250% na Satisfação do Cliente',
    ],
    website: 'https://papia.aytech.online',
    showcaseImage: '/zap.png',
    price: 'A partir de R$ 89,90/mês',
    slug: 'automacao-whatsapp',
  },
  {
    icon: <img src="/rotasicon.png" alt="Rotas" className="h-12 w-12 object-contain sm:h-16 sm:w-16" />,
    miniIcon: <img src="/rotasicon.png" alt="Rotas" className="h-8 w-8 object-contain sm:h-9 sm:w-9" />,
    title: 'Rotas',
    subtitle: 'Sistema Completo de Gestão de Entregas',
    description:
      'Gestão de entregas com mapas e rastreamento em tempo real, analytics avançados e automações integradas ao iFood e WhatsApp.',
    features: [
      'Mapa de Pedidos em Tempo Real',
      'Gestão de Motoboys',
      'Histórico Completo de Entregas',
      'Dashboard e Relatórios de Performance',
      'Mapa de Calor',
      'Integrações iFood e WhatsApp ',
    ],
    results: [
      'Até 30% de Economia em Combustível',
      'Rastreamento em Tempo Real',
      'Integração Completa com iFood',
      'Analytics Avançados de Entregas',
    ],
    website: 'https://rotas.aytech.online/',
    showcaseImage: '/rotasinfo.png',
    showcasePosition: 'left',
    showcaseSize: 'large',
    price: 'A partir de R$ 99,90/mês',
    slug: 'rotas-inteligentes',
  },
  {
    icon: <img src="/lucrumicon.png" alt="Lucrum" className="h-12 w-12 object-contain sm:h-16 sm:w-16" />,
    miniIcon: <img src="/lucrumicon.png" alt="Lucrum" className="h-8 w-8 object-contain sm:h-9 sm:w-9" />,
    title: 'Lucrum',
    subtitle: 'Gestão Financeira para Restaurantes',
    description:
      'Sistema de gestão financeira inteligente para restaurantes. Controle de caixa, despesas, folha de pagamento e lucros em um único lugar, de forma simples, rápida e eficiente.',
    features: [
      'Controle de Caixa Diário',
      'Gestão de Despesas',
      'DRE Automático',
      'Folha de Pagamento',
      'Gestão de Motoboys',
      'Relatórios Financeiros',
      'Controle de Delivery',
      'Integração com DeliveX e Rotas',
    ],
    results: [
      'Visão Completa das Finanças',
      'Controle de Lucros em Tempo Real',
      'Gestão Simplificada de Despesas',
      'Relatórios Automáticos',
    ],
    website: 'https://financeiro.aytech.online/',
    showcaseImage: '/lucruminfo.png',
    wideShowcase: true,
    showcaseSize: 'tall',
    price: 'A partir de R$ 129,90/mês',
    slug: 'lucrum-financeiro',
  },
];

// ─── Phase windows ─────────────────────────────────────────────────────────────
// Total scroll: 0 → 1 mapped across 800vh
// 0.00–0.18 : overview grid
// 0.15–0.38 : service 0 (overlap = crossfade)
// 0.35–0.58 : service 1
// 0.55–0.78 : service 2
// 0.75–1.00 : service 3
const PHASES = [
  { start: 0.15, peak: 0.22, end: 0.38 },
  { start: 0.35, peak: 0.42, end: 0.58 },
  { start: 0.55, peak: 0.62, end: 0.78 },
  { start: 0.75, peak: 0.82, end: 1.00 },
];

// ─── Overview mini-grid (phase 0) ──────────────────────────────────────────────
const OverviewGrid = ({ scrollYProgress }) => {
  // Starts fully visible (progress=0), fades out only when first service card takes over
  const opacity = useTransform(scrollYProgress, [0, 0.15, 0.22], [1, 1, 0]);
  const scale  = useTransform(scrollYProgress, [0, 0.15, 0.22], [1, 1, 0.95]);
  const y      = useTransform(scrollYProgress, [0.15, 0.22], [0, -20]);
  const smoothY = useSpring(y, { stiffness: 90, damping: 22 });

  return (
    <motion.div
      className="absolute inset-0 flex flex-col items-center justify-center px-4 z-10"
      style={{ opacity, scale, y: smoothY }}
      aria-hidden="false"
    >
      {/* Section label */}
      <div className="mb-10 text-center">
        <span className="text-xs text-white/40 uppercase tracking-widest font-mono">O que fazemos</span>
        <h2 className="text-4xl md:text-5xl font-display font-bold text-white mt-3 tracking-tighter">
          Nossas Soluções
        </h2>
        <p className="text-white/50 mt-3 text-sm max-w-sm mx-auto">
          Role para descobrir cada produto em detalhe
        </p>
      </div>

      {/* 2×2 mini glass grid — rendered immediately, no stagger delay */}
      <div className="grid grid-cols-2 gap-3 w-full max-w-lg">
        {SERVICES.map((s, i) => (
          <div
            key={i}
            className="glass-card rounded-2xl p-5 flex items-center gap-3"
          >
            <div className="flex-shrink-0">{s.miniIcon}</div>
            <div>
              <p className="text-white font-bold text-sm tracking-tight">{s.title}</p>
              <p className="text-white/70 text-xs mt-0.5">{s.subtitle}</p>
            </div>
          </div>

        ))}
      </div>

      {/* Scroll cue */}
      <motion.div
        animate={{ y: [0, 6, 0] }}
        transition={{ duration: 1.8, repeat: Infinity }}
        className="mt-10 flex flex-col items-center gap-2"
      >
        <p className="text-white/30 text-xs uppercase tracking-widest">Role para explorar</p>
        <div className="w-5 h-8 border border-white/20 rounded-full flex justify-center">
          <div className="w-0.5 h-2.5 bg-white/30 rounded-full mt-1.5" />
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── Mobile services list ──────────────────────────────────────────────────────
// Parallax and nested scrolling compete for the same gesture on touchscreens.
// Mobile therefore uses the same content in a natural document flow.
const MobileServicesList = () => (
  <section className="relative z-10 px-4 py-20 lg:hidden" aria-labelledby="mobile-services-title">
    <div className="mx-auto max-w-xl">
      <div className="mb-8 text-center">
        <span className="font-mono text-xs uppercase tracking-widest text-white/60">O que fazemos</span>
        <h2 id="mobile-services-title" className="mt-2 font-display text-3xl font-bold tracking-tighter text-white">
          Nossas Soluções
        </h2>
        <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-white/70">
          Conheça as soluções que conectam atendimento, operação e gestão.
        </p>
      </div>

      <div className="space-y-6">
        {SERVICES.map((service) => (
          <article
            key={service.slug}
            className="service-detail-card glass-card rounded-3xl p-5 sm:p-7"
            aria-labelledby={`mobile-service-${service.slug}`}
          >
            <div className="mb-5 flex items-center gap-3">
              <div className="flex-shrink-0">{service.icon}</div>
              <div className="min-w-0">
                <h3
                  id={`mobile-service-${service.slug}`}
                  className="font-display text-2xl font-bold tracking-tight text-white"
                >
                  {service.title}
                </h3>
                <p className="mt-1 font-mono text-[10px] uppercase leading-4 tracking-widest text-white/75">
                  {service.subtitle}
                </p>
              </div>
            </div>

            <p className="mb-6 text-[15px] leading-6 text-white/90">{service.description}</p>

            <ul className="mb-6 space-y-3">
              {service.features.map((feature) => (
                <li key={feature} className="flex items-start gap-2 text-sm leading-5 text-white/85">
                  <FiCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-white/80" aria-hidden="true" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            {service.closingText && (
              <p className="mb-6 text-sm font-semibold leading-6 text-white/90">{service.closingText}</p>
            )}

            {service.results && (
              <div className="mb-6 border-t border-white/15 pt-5">
                <p className="mb-3 font-mono text-[10px] uppercase tracking-widest text-white/75">
                  Resultados comprovados
                </p>
                <ul className="space-y-2.5">
                  {service.results.map((result) => (
                    <li key={result} className="text-[13px] font-medium leading-5 text-white/85">
                      {result}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex flex-col gap-4">
              <span className="text-sm font-medium text-white/90">{service.price}</span>
              {service.website ? (
                <a
                  href={service.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-xs font-semibold uppercase tracking-widest text-gray-950 transition-colors duration-300 hover:bg-white/90"
                >
                  Saiba Mais
                  <FiArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
              ) : (
                <Link
                  to={`/services/${service.slug}`}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-xs font-semibold uppercase tracking-widest text-gray-950 transition-colors duration-300 hover:bg-white/90"
                >
                  Saiba Mais
                  <FiArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                </Link>
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

// ─── Individual service card (full-screen detail) ──────────────────────────────
const ServicePhaseCard = ({ service, phase, index, scrollYProgress }) => {
  const { start, peak, end } = phase;
  const fadeEnd = Math.min(end, 1);
  const fadeStart = Math.max(peak, fadeEnd - 0.035);

  const opacity = useTransform(
    scrollYProgress,
    [start, peak, fadeStart, fadeEnd],
    [0, 1, 1, index === 3 ? 1 : 0]   // last card stays visible
  );
  const scale = useTransform(
    scrollYProgress,
    [start, peak, fadeStart, fadeEnd],
    [0.86, 1, 1, index === 3 ? 1 : 1.04]
  );
  const y = useTransform(
    scrollYProgress,
    [start, peak],
    [50, 0]
  );
  const smoothY = useSpring(y, { stiffness: 90, damping: 22 });
  const nextPhaseStart = PHASES[index + 1]?.start ?? 1.01;
  const pointerEvents = useTransform(scrollYProgress, (value) =>
    value >= peak && value < nextPhaseStart ? 'auto' : 'none'
  );
  const zIndex = useTransform(scrollYProgress, (value) =>
    value >= peak && value < nextPhaseStart ? 20 : 0
  );

  return (
    <motion.div
      className="absolute inset-0 flex items-center justify-center px-4 sm:px-8 lg:px-20 z-10"
      style={{ opacity, scale, y: smoothY, pointerEvents, zIndex }}
    >
      {/* Glass detail card */}
      <div
        className={`glass-card w-full rounded-3xl ${
          service.showcaseImage
            ? service.showcasePosition === 'left'
              ? service.showcaseSize === 'large'
                ? 'max-w-7xl p-8 md:p-10'
                : service.showcaseSize === 'square'
                  ? 'max-w-7xl p-8 md:p-10'
                  : 'max-w-6xl p-8 md:p-10'
              : service.showcaseSize === 'tall'
                ? 'max-w-6xl p-8 md:p-10'
                : 'max-w-5xl p-8 md:p-10'
            : 'max-w-2xl p-8 md:p-12'
        }`}
      >
        <div
          className={service.showcaseImage
            ? service.showcasePosition === 'left'
              ? service.showcaseSize === 'large'
                ? 'grid items-center gap-8 lg:grid-cols-[420px_minmax(0,1fr)] xl:grid-cols-[560px_minmax(0,1fr)] xl:gap-10'
                : service.showcaseSize === 'square'
                  ? 'grid items-stretch gap-8 lg:grid-cols-[460px_minmax(0,1fr)] xl:grid-cols-[500px_minmax(0,1fr)] xl:gap-10'
                  : 'grid items-center gap-8 lg:grid-cols-[420px_minmax(0,1fr)] xl:grid-cols-[460px_minmax(0,1fr)] xl:gap-10'
              : service.showcaseSize === 'tall'
                ? 'grid items-stretch gap-8 lg:grid-cols-[minmax(0,1fr)_400px] xl:grid-cols-[minmax(0,1fr)_420px] xl:gap-10'
                : service.wideShowcase
                  ? 'grid items-center gap-8 lg:grid-cols-[minmax(0,1fr)_300px] xl:grid-cols-[minmax(0,1fr)_340px] xl:gap-10'
                  : 'grid items-center gap-8 lg:grid-cols-[minmax(0,1fr)_230px] xl:grid-cols-[minmax(0,1fr)_250px] xl:gap-10'
            : ''}
        >
          <div className={`min-w-0 ${service.showcasePosition === 'left' ? 'lg:order-2' : ''}`}>

            {/* Service heading */}
            <div className="mb-8 flex items-center gap-5">
              <div className="flex-shrink-0">{service.icon}</div>
              <div className="min-w-0">
                {service.website ? (
                  <a
                    href={service.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`Abrir a página do ${service.title}`}
                    className="inline-block"
                  >
                    <h3 className="font-display text-4xl font-bold tracking-tighter text-white transition-colors hover:text-white/75 md:text-5xl">
                      {service.title}
                    </h3>
                  </a>
                ) : (
                  <h3 className="font-display text-4xl font-bold tracking-tighter text-white md:text-5xl">
                    {service.title}
                  </h3>
                )}
                <p className="mt-1 font-mono text-xs uppercase tracking-widest text-white/70">
                  {service.subtitle}
                </p>
              </div>
            </div>

            {/* Description */}
            <p className="text-white/80 text-base leading-relaxed mb-8">
              {service.description}
            </p>

            {/* Features grid */}
            <div className="grid grid-cols-2 gap-2.5 mb-8">
              {service.features.map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-white/70">
                  <FiCheck className="w-3.5 h-3.5 text-white/70 flex-shrink-0" />
                  {f}
                </div>
              ))}
            </div>

            {service.closingText && (
              <p className="mb-8 text-sm font-semibold leading-relaxed text-white/85">
                {service.closingText}
              </p>
            )}

            {/* Optional performance highlights */}
            {service.results && (
              <div className="mb-8 border-t border-white/10 pt-5">
                <p className="mb-3 font-mono text-[10px] uppercase tracking-widest text-white/70">
                  Resultados comprovados
                </p>
                <div className="grid grid-cols-2 gap-x-5 gap-y-2">
                  {service.results.map((result, i) => (
                    <div key={i} className="text-xs font-medium leading-relaxed text-white/75">
                      {result}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Price + CTA */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <span className="text-white/70 text-sm font-medium">{service.price}</span>
              {service.website ? (
                <a
                  href={service.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-auto inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-xs font-semibold uppercase tracking-widest text-gray-950 transition-all duration-300 hover:gap-3 hover:bg-white/90"
                >
                  Saiba Mais
                  <FiArrowRight className="w-3.5 h-3.5" />
                </a>
              ) : (
                <Link
                  to={`/services/${service.slug}`}
                  className="ml-auto inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-xs font-semibold uppercase tracking-widest text-gray-950 transition-all duration-300 hover:gap-3 hover:bg-white/90"
                >
                  Saiba Mais
                  <FiArrowRight className="w-3.5 h-3.5" />
                </Link>
              )}
            </div>
          </div>

          {service.showcaseImage && (
            <div
              className={`hidden h-full justify-center lg:flex ${
                service.showcaseAlign === 'start' ? 'items-start' : 'items-center'
              } ${
                service.showcasePosition === 'left' ? 'lg:order-1' : ''
              }`}
            >
              <img
                src={service.showcaseImage}
                alt={`Demonstração do ${service.title}`}
                className={`w-full rounded-2xl border border-white/10 object-center shadow-2xl ${
                  service.showcaseSize === 'tall' || service.showcaseSize === 'square'
                    ? 'h-full object-fill'
                    : 'h-auto max-h-[490px] object-contain'
                }`}
              />
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// ─── Progress dot strip ────────────────────────────────────────────────────────
const ProgressStrip = ({ scrollYProgress }) => {
  // compute 4 separate opacity values (no hooks in map)
  const d0 = useTransform(scrollYProgress, [0.15, 0.22, 0.345, 0.38], [0.3, 1, 1, 0.3]);
  const d1 = useTransform(scrollYProgress, [0.35, 0.42, 0.545, 0.58], [0.3, 1, 1, 0.3]);
  const d2 = useTransform(scrollYProgress, [0.55, 0.62, 0.745, 0.78], [0.3, 1, 1, 0.3]);
  const d3 = useTransform(scrollYProgress, [0.75, 0.82, 1.00, 1.00], [0.3, 1, 1, 1.0]);
  const dots = [d0, d1, d2, d3];
  const sW0 = useTransform(scrollYProgress, [0.15, 0.22, 0.345, 0.38], ['6px', '18px', '18px', '6px']);
  const sW1 = useTransform(scrollYProgress, [0.35, 0.42, 0.545, 0.58], ['6px', '18px', '18px', '6px']);
  const sW2 = useTransform(scrollYProgress, [0.55, 0.62, 0.745, 0.78], ['6px', '18px', '18px', '6px']);
  const sW3 = useTransform(scrollYProgress, [0.75, 0.82, 1.00, 1.00], ['6px', '18px', '18px', '18px']);
  const widths = [sW0, sW1, sW2, sW3];

  // overview dot
  const ovOp = useTransform(scrollYProgress, [0, 0.05, 0.18, 0.24], [0.3, 1, 1, 0.3]);
  const ovW  = useTransform(scrollYProgress, [0, 0.06, 0.18, 0.24], ['6px', '18px', '18px', '6px']);

  return (
    <motion.div
      className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-20"
      style={{ opacity: 1 }}
    >
      {/* Overview dot */}
      <motion.div
        className="h-1.5 rounded-full bg-white"
        style={{ opacity: ovOp, width: ovW }}
      />
      {dots.map((op, i) => (
        <motion.div
          key={i}
          className="h-1.5 rounded-full bg-white"
          style={{ opacity: op, width: widths[i] }}
        />
      ))}
    </motion.div>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────
const ServicesParallax = () => {
  const containerRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });

  return (
    <>
      <MobileServicesList />

      {/* 800vh gives each desktop service a comfortable full-opacity reading window */}
      <section ref={containerRef} style={{ height: '800vh' }} className="relative hidden lg:block">

        {/* Sticky viewport */}
        <div className="sticky top-0 h-screen overflow-hidden">

        {/* SVG glass distortion filter */}
        <GlassFilter />

        {/* ── Overview ── */}
        <OverviewGrid scrollYProgress={scrollYProgress} />

        {/* ── Individual service cards ── */}
        {SERVICES.map((service, i) => (
          <ServicePhaseCard
            key={i}
            service={service}
            phase={PHASES[i]}
            index={i}
            scrollYProgress={scrollYProgress}
          />
        ))}

        {/* ── Progress indicator ── */}
        <ProgressStrip scrollYProgress={scrollYProgress} />
        </div>
      </section>
    </>
  );
};

export default ServicesParallax;
